import * as React from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { useAppSelector } from '../../../app/hooks';
import { useLoadLeagueData, useLoadTradeData, useAddUpdateTrade } from '../../../app/dataLoaderHooks';
import { League, Team as SDKTeam, TeamPlayerInfo, DraftPick, TradeProposal, TradeProposalTeam, TradeProposalPlayer, TradeProposalPick, PublicUserInfo } from '../../../sdk/model';
import TradeTeamViewer from './TradeTeamViewer';
import TradeDetails from './TradeDetails';
import { getMaxTeamSize, getUsersTeamFromLeague as getTeamName } from '../../util/TeamUtils';
import { 
  Avatar, 
  Box, 
  Typography, 
  Paper, 
  Grid, 
  IconButton, 
  Button, 
  Select, 
  MenuItem, 
  FormControl, 
  SelectChangeEvent, 
  Tooltip,
  Collapse
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { AxiosPromise } from 'axios';
import { tradeApi } from '../../../adapters/APIExporter';
import ReturnFromIrTradeOption from './ReturnFromIrTradeOption';
import AdvancedTradeOptions from './AdvancedTradeOptions';
// Inline ErrorMessage component since we can't find the module

// Extend the SDK Team type with additional properties
interface Team extends Omit<SDKTeam, 'owners'> {
  rosterLoaded?: boolean;
  owners?: Array<PublicUserInfo & { rosterLoaded?: boolean }>;
}

// Helper function to find default team
function findDefaultTeam(teams: Team[], userId: string | undefined): string | undefined {
  if (!teams || !userId) return undefined;
  const userTeam = teams.find(team => 
    team.owners?.some(owner => owner?.id === userId)
  );
  return userTeam?.id;
}

// Helper function to get team display name
function getTeamDisplayName(team: Team | undefined, teamMap: Map<string | undefined, Team>, userId?: string): string {
  if (!team) return 'Unknown Team';
  if (team.name) return team.name;
  if (team.id) return `Team ${team.id.substring(0, 6)}`;
  return 'Unnamed Team';
}
const ErrorMessage = ({ message }: { message: string }) => (
  <Typography color="error">{message}</Typography>
);

function TradeBuilder() {
  const navigate = useNavigate();
  const {leagueId, tradeId} = useParams();
  const [searchParams] = useSearchParams();
  const otherTeamId = searchParams.get("teamId")
  // Initialize trade team IDs with an empty array if no otherTeamId is provided
  const [tradeTeamIds, setTradeTeamIds] = React.useState<string[]>(['']);
  
  // Track expanded state for each team's collapsible section
  const [expandedTeams, setExpandedTeams] = React.useState<Record<string, boolean>>({
    myTeam: false // Start with myTeam collapsed by default
  });
  
  const [allInvolvedTeamIds, setAllInvolvedTeamIds] = React.useState([]);
  
  // Redux store variables
  const user = useAppSelector((state) => state.user);
  const playerDataLoad = useAppSelector((state) => state.playerDataLoad );

  const [counterId, setCounterId] = React.useState(null);
  const [tradeLoaded, setTradeLoaded] = React.useState(tradeId ? false : true)
  const [errors, setErrors] = React.useState([]);
  const [players, setPlayers] = React.useState<TradeProposalPlayer[] | undefined>([]);
  const [picks, setPicks] = React.useState([]);
  const [selectedPlayerSetup, setSelectedPlayerSetup] = React.useState(false);
  
  const [isTradeLoadComplete, setTradeLoadComplete] = React.useState(tradeId ? false : true);
  const [isException, setIsException] = React.useState(false);
  const [updatedTrade, setUpdatedTrade] = React.useState(undefined);
  
  const [isLoadComplete, setLoadComplete] = React.useState(false);
    
  // Create player map safely, handling potential undefined values
  let playerMap = new Map<string, TeamPlayerInfo>();
  if (playerDataLoad) {
    const allPlayers = (playerDataLoad || [])
      .flatMap(team => team?.players || [])
      .filter((player): player is NonNullable<typeof player> => Boolean(player && player.id));
    playerMap = new Map(allPlayers.map(player => [player.id, player]));
  }

  const leagueDataParams: LoadLeagueSeasonData = {
    leagueId: leagueId,
    userId: user?.id,
    loadBasicDraftData: true,
    loadFullDraftData: false,
    loadContractData: true,
    loadBasicAuctionData: true,
    loadFullAuctionData: false,
    loadRosterData: false,
    loadRosterFor: allInvolvedTeamIds,
    loadWaivedContracts: false,
    loadScheduleData: false,
    isException: isException,
    setIsException: setIsException,
    isLoadComplete: isLoadComplete,
    setLoadComplete: setLoadComplete
  };
  
  const league: League | undefined = useLoadLeagueData(leagueDataParams);

  const teamMap = new Map<string, Team>();
  const teams = league?.teams || [];
  
  teams.forEach(team => {
    if (team.id) {
      teamMap.set(team.id, team as Team);
    }
  });
  
  const myTeam = teams.length ? teamMap.get(findDefaultTeam(teams as Team[], user?.id) || '') : undefined;
  
  const trades : Array<TradeProposal> = useLoadTradeData({teamId: myTeam?.id!, setIsException: setIsException, isLoadComplete: isTradeLoadComplete, setLoadComplete: setTradeLoadComplete});
    
  React.useEffect(() => {
    if (myTeam) {
      setAllInvolvedTeamIds([myTeam?.id].concat(tradeTeamIds));
    }
  }, [myTeam, tradeTeamIds]);
  
  React.useEffect(() => {
      if (tradeId) {
        tradeApi.getTradeProposal(tradeId).then(response => {
          const tradeResponse = response.data;
          setTradeTeamIds(tradeResponse.teams.map(team => team.teamId).filter(teamId => teamId !== myTeam.id))
          setPlayers(tradeResponse.players);
          setPicks(tradeResponse.picks);
          if (tradeResponse.status === 'PENDING') {
            setCounterId(tradeId);
          }
          setTradeLoaded(true);
        });
      }
    }, []);
  
  
  // If teamId is in the URL, update tradeTeamIds and add player to trade
  React.useEffect(() => {
    const teamId = searchParams.get("teamId");
    const playerId = searchParams.get("playerId");

    if (teamId) {
      // Update tradeTeamIds with the teamId from URL if it exists
      setTradeTeamIds([teamId]);
      
      // Set the team as expanded when selected
      const teamKey = `trade-${teamId}-0`;
      setExpandedTeams(prev => ({
        ...prev,
        [teamKey]: true
      }));
      
      // If there's also a playerId, add the player to the trade
      if (playerId && !players?.some(player => player.playerId === playerId)) {
        setPlayers(prevPlayers => [
          ...(prevPlayers || []),
          {
            playerId: playerId,
            fromTeamId: teamId, // The team that currently owns the player
            toTeamId: myTeam?.id, // The player is being traded to my team
            action: 'TRADE',
            tradingTeamDeadCapPayouts: {}
          } as TradeProposalPlayer
        ]);
      }
    }
  }, [searchParams, myTeam?.id]); // Only run when component mounts or searchParams changes
  
  useAddUpdateTrade(myTeam?.id!, updatedTrade?.id, updatedTrade);
  
  if (updatedTrade) {
    navigate("/league/" + league?.id + "/trade/" + updatedTrade.id);
    setUpdatedTrade(undefined);
  }

  // Toggle collapsible section
  const toggleTeamExpand = (teamId: string) => {
    setExpandedTeams(prev => ({
      ...prev,
      [teamId]: !prev[teamId]
    }));
  };

  // Check if my team is loaded
  const isMyTeamLoaded = React.useMemo(() => {
    return tradeLoaded && league && user && myTeam?.rosterLoaded;
  }, [tradeLoaded, league, user, myTeam]);

  // Check if a specific trade team is loaded
  const isTradeTeamLoaded = React.useCallback((teamId: string) => {
    const team = teamMap.get(teamId);
    return team?.rosterLoaded;
  }, [teamMap]);

  // Wait for my team to load before showing the page
  if (!isMyTeamLoaded || !myTeam) {
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        justifyContent: 'center', 
        minHeight: '300px',
        textAlign: 'center',
        p: 3
      }}>
        <Typography variant="h6" color="textSecondary" gutterBottom>
          Loading your team data...
        </Typography>
        <Typography variant="body2" color="textSecondary">
          Please wait while we load your team roster.
        </Typography>
      </Box>
    );
  }
  
  let errorInfo = Object.entries(errors).map(([key, value]) => {
    if (value) {
      return (<p className='error' key={'error_' + key}>{value}</p>);
    }
    return (<p className='error' key={'error_' + key}></p>);
  });
  
  const teamPlayerMap = new Map<string, TeamPlayerInfo>(
    league?.teams
      ?.filter(team => team?.playerRoster)
      .flatMap(team => 
        Object.values(team.playerRoster)
          .flatMap(playerList => 
            (playerList || [])
              .filter((player): player is TeamPlayerInfo => !!player)
              .map(player => [player.playerId, player] as [string, TeamPlayerInfo])
          )
      ) || []
  );
  
  const pickMap = new Map<string, DraftPick>(
    league?.teams
      ?.filter(team => team?.draftPicks?.length)
      .flatMap(team => 
        (team.draftPicks || [])
          .filter((pick): pick is DraftPick => !!pick)
          .map(pick => [pick.id, pick] as [string, DraftPick])
      ) || []
  );
  
  const proposalPlayerMap = new Map<string, TradeProposalPlayer>(
    players.map(player => [player.playerId, player] as [string, TradeProposalPlayer])
  );
  
  const proposalPickMap = new Map<string, TradeProposalPick>(
    picks.map(pick => [pick.draftPickId, pick] as [string, TradeProposalPick])
  );
  
  const allInvolvedTeams = allInvolvedTeamIds.map(teamId => teamMap.get(teamId)).filter(team => !team || team.rosterLoaded);
  
  function handleTradeProposalChange(assetId: string, fromTeamId: string, action: string, assetType: string) {
    if (assetType === "player") {
      handleAssetChange(players, assetId, "playerId", action, fromTeamId, setPlayers);
    } else if (assetType === "pick") {
      handleAssetChange(picks, assetId, "draftPickId", action, fromTeamId, setPicks);
    }
  }
  
  function handleAssetChange(assetList, assetId, idFieldName, action, fromTeamId, assetListSetter) {
    const newAssetList = [...assetList];
    const assetIndex = newAssetList.map(asset => asset[idFieldName]).indexOf(assetId);
    if (action === 'default') {
      if (~assetIndex) {
        newAssetList.splice(assetIndex, 1);
      }
    } else {
      let actionName: string;
      let toTeamId : string | null;
      if (action === 'drop') {
        actionName = 'DROP';
        toTeamId = null;
      } else {
        actionName = 'TRADE';
        toTeamId = action;
      }
      let asset;
      if (~assetIndex) {
        asset = newAssetList[assetIndex];
      } else {
        asset = {
          fromTeamId: fromTeamId
        };
        asset[idFieldName] = assetId;
        newAssetList.push(asset);
      }
      asset.action = actionName;
      asset.toTeamId = toTeamId;
      asset.tradingTeamDeadCapPayouts = {};
    }
    assetListSetter(newAssetList);
  }
  
  function handleDeadCapChange(playerId: string, contractSeason: number, tradingTeamDeadCapPayout: number) {
    const newPlayers = players?.map(player => {
      const newPlayer = {...player};
      if (newPlayer.playerId === playerId) {
        newPlayer.tradingTeamDeadCapPayouts![contractSeason.toString()] = tradingTeamDeadCapPayout;
      }
      return newPlayer;
    })
    setPlayers(newPlayers);
  }
  
  function clearDraft() {
    setTradeTeamIds([""]);
    setPlayers([]);
    setPicks([]);
  }
  
  // Function to add a new trade team
  const addTeam = () => {
    if (!league?.teams?.length) return;
    
    // Add an empty string to trigger the team selection dropdown
    setTradeTeamIds(prev => [...prev, '']);
  };
  
  function saveDraft() {
    const newErrors : Array<string> = [];
    const otherTeamIds: Array<string> = tradeTeamIds.filter(tradeTeamId => tradeTeamId && players.concat(picks).some(asset => asset.fromTeamId === tradeTeamId || asset.toTeamId === tradeTeamId))
    if (otherTeamIds.length === 0) {
      newErrors.push("There must be at least two teams involved in a trade");
    }
    setErrors(newErrors);
    if (newErrors.length > 0) {
      return;
    }
    const teams : Array<TradeProposalTeam> = [{
      teamId: myTeam?.id!,
      status: 'PROPOSED'
    }].concat(otherTeamIds.map(tradeTeamId => {
      return {
        teamId: tradeTeamId!,
        status: 'PENDING'
      }
    }));
    const tradeProposal : TradeProposal = {
      teams: teams,
      players: players,
      picks: picks,
      status: 'DRAFT'
    }
    createOrUpdateAndNavigate(tradeId ? 
        (counterId ? 
          tradeApi.counterTradeProposal(tradeProposal, myTeam?.id, counterId) :
          tradeApi.updateTradeProposal(tradeProposal, tradeId)
        ) :  
        tradeApi.createTradeProposal(tradeProposal, myTeam?.id));
  }
  
  function createOrUpdateAndNavigate(promise: AxiosPromise<string>) {
    promise.then((response) => {
      setUpdatedTrade(response.data);
    }).catch((error) => {
      let newErrors = [error.response.data ? error.response.data.message : "Request failed.  Please try again."];
      setErrors(newErrors);
    });
  }
  
  const maxRosterSize = getMaxTeamSize(league!);
  
  // Calculate cap space for a team
  const getTeamCapSpace = (team: any) => {
    if (!team || team.currentSeasonSalary === undefined || !league?.currentSeason?.nflSeason?.salaryCap) return null;
    return (league.currentSeason.nflSeason.salaryCap ?? 0) - team.currentSeasonSalary;
  };
  
  // Format cap space for display
  const formatCapSpace = (capSpace: number | null) => {
    if (capSpace === null) return 'N/A';
    const formatted = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(Math.abs(capSpace));
    
    return `${capSpace >= 0 ? '+' : '-'}${formatted}`;
  };
  
  // Get cap space color based on value
  const getCapSpaceColor = (capSpace: number | null) => {
    if (capSpace === null) return 'text.secondary';
    return capSpace >= 0 ? 'success.light' : 'error.light';
  };
  
  // Cap space info tooltip
  const CapSpaceInfo = () => (
    <Tooltip 
      title={
        <>
          <div>Salary Cap: {new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
          }).format(league?.currentSeason?.nflSeason?.salaryCap || 0)}</div>
          {myTeam && (
            <div>Team Salary: {new Intl.NumberFormat('en-US', {
              style: 'currency',
              currency: 'USD',
              minimumFractionDigits: 0,
              maximumFractionDigits: 0
            }).format(myTeam.currentSeasonSalary || 0)}</div>
          )}
        </>
      }
      arrow
      placement="top"
    >
      <InfoOutlinedIcon fontSize="small" sx={{ ml: 0.5, opacity: 0.7, '&:hover': { opacity: 1 } }} />
    </Tooltip>
  );
  
  // Function to remove a trade team and clean up associated assets
  const handleRemoveTeam = (index: number) => {
    const teamIdToRemove = tradeTeamIds[index];
    
    // If it's an empty team (no ID), just remove it from the array
    if (!teamIdToRemove) {
      setTradeTeamIds(prev => {
        const newTeamIds = [...prev];
        newTeamIds.splice(index, 1);
        return newTeamIds;
      });
      return;
    }
    
    // Remove the team from the trade
    setTradeTeamIds(prev => {
      const newTeamIds = [...prev];
      newTeamIds.splice(index, 1);
      return newTeamIds;
    });
    
    // Clean up any players or picks associated with this team
    setPlayers((prevPlayers: any[] = []) => 
      prevPlayers.filter((p: any) => 
        p.fromTeamId !== teamIdToRemove && p.toTeamId !== teamIdToRemove
      )
    );
    
    setPicks((prevPicks: any[] = []) => 
      prevPicks.filter((p: any) => 
        p.fromTeamId !== teamIdToRemove && p.toTeamId !== teamIdToRemove
      )
    );
  };

  // Handle trade team selection change
  const handleTeamChange = (event: SelectChangeEvent<string>, index: number) => {
    const newTeamId = event.target.value;

    // If the user is selecting "Select a team...", don't add it to the trade
    if (!newTeamId) return;
        
    // Set the team as expanded when selected
    const teamKey = `trade-${newTeamId}-${index}`;
    setExpandedTeams(prev => ({
      ...prev,
      [teamKey]: true
    }));
    
    // Check if this team is already selected
    if (tradeTeamIds.includes(newTeamId)) {
      // If already selected, remove it
      setTradeTeamIds(prev => prev.filter((_, i) => i !== index));
      return;
    }
    
    // Update the team at the specified index
    setTradeTeamIds(prev => {
      const newTeamIds = [...prev];
      newTeamIds[index] = newTeamId;
      
      // If this is the first team being added, expand the trade details
      if (prev.length === 0) {
        setExpandedTeams(prevExpanded => ({
          ...prevExpanded,
          'myTeam': true
        }));
      }
      
      // Expand the newly selected team
      setExpandedTeams(prev => ({
        ...prev,
        [`trade-${newTeamId}`]: true
      }));
      
      return newTeamIds;
    });
  };

  return (
    <Box>
      <Button color="primary" variant="contained" onClick={clearDraft} sx={{ mb: 2 }}>
        Clear
      </Button>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Paper elevation={2}>
            <Box 
              onClick={() => toggleTeamExpand('myTeam')}
              sx={{
                p: 2,
                backgroundColor: 'primary.main',
                color: 'primary.contrastText',
                cursor: 'pointer',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                borderRadius: 1,
                '&:hover': {
                  backgroundColor: 'primary.dark',
                },
                transition: 'background-color 0.2s ease-in-out',
                boxShadow: 1
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                <Avatar 
                  src={myTeam?.picture} 
                  sx={{ width: 32, height: 32 }}
                  alt={myTeam?.name || 'My Team'}
                />
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexGrow: 1, gap: 1 }}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h6">
                      {getTeamDisplayName(myTeam, teamMap, user?.id)}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mt: 0.5 }}>
                      <Typography variant="caption" sx={{ opacity: 0.8, whiteSpace: 'nowrap' }}>
                        {(players as Array<TradeProposalPlayer>).filter(p => p.fromTeamId === myTeam?.id).length} players • 
                        {(picks as Array<TradeProposalPick>).filter(p => p.fromTeamId === myTeam?.id).length} picks
                      </Typography>
                      {myTeam && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Typography 
                            variant="caption" 
                            sx={{ 
                              fontWeight: 'medium',
                              color: getCapSpaceColor(getTeamCapSpace(myTeam)),
                              backgroundColor: 'rgba(0, 0, 0, 0.3)',
                              px: 0.75,
                              py: 0.25,
                              borderRadius: 1
                            }}
                          >
                            {formatCapSpace(getTeamCapSpace(myTeam))}
                          </Typography>
                          <CapSpaceInfo />
                        </Box>
                      )}
                    </Box>
                  </Box>
                </Box>

              </Box>
              {expandedTeams['myTeam'] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </Box>
            <Collapse in={expandedTeams['myTeam'] !== false}>
              <Box p={2}>
                {isMyTeamLoaded ? (
                  <TradeTeamViewer 
                    team={myTeam} 
                    currentSeason={league?.currentSeason?.seasonYear!}
                    playerMap={playerMap} 
                    teamMap={teamMap} 
                    userId={user?.id} 
                    myTeam={true} 
                    otherTeamIds={tradeTeamIds.filter((tradeTeamId): tradeTeamId is string => Boolean(tradeTeamId))} 
                    handleTradeProposalChange={handleTradeProposalChange}
                    proposalPlayerMap={proposalPlayerMap} 
                    proposalPickMap={proposalPickMap} 
                    earliestLockDate={null}
                    practiceSquad={false}
                  />
                ) : (
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center', 
                    minHeight: '200px',
                    textAlign: 'center',
                    p: 3
                  }}>
                    <Typography variant="body1" color="textSecondary">
                      Loading your team roster...
                    </Typography>
                  </Box>
                )}
              </Box>
            </Collapse>
          </Paper>
        </Grid>

        {/* Trade Partner Sections */}
        {tradeTeamIds.map((tradeTeamId, tradeTeamIndex) => {
          const tradeTeam = teamMap.get(tradeTeamId);
          const otherTeamIds = [myTeam?.id].concat(tradeTeamIds).filter(otherTeamId => otherTeamId && otherTeamId !== tradeTeamId);
          const teamKey = `trade-${tradeTeamId || 'new'}-${tradeTeamIndex}`;
          
          // If we have an empty team ID, show the team selection dropdown
          if (!tradeTeamId) {
            return (
              <Grid key={teamKey} item xs={12}>
                <Paper elevation={2} sx={{ p: 2, mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography variant="subtitle1" fontWeight="bold">
                      Select a team to trade with:
                    </Typography>
                    <FormControl size="small" sx={{ minWidth: 200 }}>
                      <Select
                        value=""
                        onChange={(e) => handleTeamChange(e, tradeTeamIndex)}
                        displayEmpty
                        inputProps={{ 'aria-label': 'Select team' }}
                      >
                        <MenuItem value="" disabled>
                          Select a team...
                        </MenuItem>
                        {league?.teams
                          ?.filter(team => team.id !== myTeam?.id && !tradeTeamIds.includes(team.id!))
                          .map(team => (
                            <MenuItem key={team.id} value={team.id}>
                              {getTeamDisplayName(team, teamMap, user?.id)}
                            </MenuItem>
                          ))}
                      </Select>
                    </FormControl>
                    <IconButton 
                      onClick={() => handleRemoveTeam(tradeTeamIndex)}
                      size="small"
                      sx={{ ml: 'auto' }}
                    >
                      <CloseIcon />
                    </IconButton>
                  </Box>
                </Paper>
              </Grid>
            );
          }
          
          return (
            <Grid key={teamKey} item xs={12}>
              <Paper elevation={2}>
                <Box 
                  sx={{
                    p: 2,
                    backgroundColor: `var(--division-${(tradeTeamIndex % 4) + 1}-color)`,
                    color: 'secondary.contrastText',
                    borderRadius: 1,
                    boxShadow: 1,
                    mb: 1,
                    position: 'relative',
                    '&:hover .trade-actions': {
                      opacity: 1
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                    <Avatar 
                      src={teamMap.get(tradeTeamId)?.picture} 
                      sx={{ width: 32, height: 32 }}
                      alt={teamMap.get(tradeTeamId)?.name || 'Team'}
                    />
                    <FormControl size="small" sx={{ flexGrow: 1, mx: 2 }}>
                      <Select
                        value={tradeTeamId || ''}
                        onChange={(e) => handleTeamChange(e, tradeTeamIndex)}
                        displayEmpty
                        inputProps={{ 'aria-label': 'Select team' }}
                        sx={{
                          '& .MuiSelect-select': {
                            textAlign: 'center',
                            fontWeight: 'bold',
                            fontSize: '1.25rem',
                            lineHeight: 1.2,
                            py: 1,
                            '&:focus': {
                              backgroundColor: 'transparent'
                            }
                          },
                          '& .MuiOutlinedInput-notchedOutline': {
                            border: 'none'
                          },
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            border: 'none'
                          },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            border: 'none',
                            boxShadow: 'none'
                          },
                          '& .MuiSelect-icon': {
                            color: 'inherit'
                          }
                        }}
                        IconComponent={ExpandMoreIcon}
                      >
                        <MenuItem value="" disabled>
                          Select Team
                        </MenuItem>
                        {league?.teams
                          ?.filter(team => team.id !== myTeam?.id && (team.id === tradeTeamId || !tradeTeamIds.includes(team.id!)))
                          .map(team => (
                            <MenuItem key={team.id} value={team.id}>
                              {getTeamDisplayName(team, teamMap, user?.id)}
                            </MenuItem>
                          ))}
                      </Select>
                    </FormControl>

                    
                    <IconButton 
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRemoveTeam(tradeTeamIndex);
                      }}
                      size="small"
                      className="trade-actions"
                      sx={{
                        position: 'absolute',
                        right: 8,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        color: 'secondary.contrastText',
                        opacity: 1, // Keep it always visible
                        transition: 'opacity 0.2s ease-in-out',
                        '&:hover': {
                          backgroundColor: 'rgba(255, 255, 255, 0.2)'
                        },
                        zIndex: 1 // Ensure it's above the select
                      }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Box>
                  
                  {/* Team Summary */}
                  <Box 
                    onClick={() => toggleTeamExpand(teamKey)}
                    sx={{
                      cursor: 'pointer',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      mt: 1,
                      pt: 1,
                      borderTop: '1px solid rgba(255, 255, 255, 0.1)'
                    }}
                  >
                    <Box sx={{ width: '100%' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                        <Typography variant="caption" sx={{ opacity: 0.8, whiteSpace: 'nowrap' }}>
                          {players?.filter((p: any) => p.fromTeamId === tradeTeamId).length || 0} players • 
                          {picks?.filter((p: any) => p.fromTeamId === tradeTeamId).length || 0} picks
                        </Typography>
                        {tradeTeam && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Typography 
                              variant="caption" 
                              sx={{ 
                                fontWeight: 'medium',
                                color: getCapSpaceColor(getTeamCapSpace(tradeTeam)),
                                backgroundColor: 'rgba(0, 0, 0, 0.3)',
                                px: 0.75,
                                py: 0.25,
                                borderRadius: 1,
                                whiteSpace: 'nowrap'
                              }}
                            >
                              Cap: {formatCapSpace(getTeamCapSpace(tradeTeam))}
                            </Typography>
                            <CapSpaceInfo />
                          </Box>
                        )}
                      </Box>
                    </Box>
                    {expandedTeams[teamKey] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </Box>
                </Box>
                <Collapse in={expandedTeams[teamKey] !== false}>
                  <Box p={2}>
                    {isTradeTeamLoaded(tradeTeamId) ? (
                      <TradeTeamViewer 
                        team={tradeTeam} 
                        currentSeason={league?.currentSeason?.seasonYear!}
                        playerMap={playerMap} 
                        teamMap={teamMap} 
                        userId={user?.id} 
                        myTeam={false} 
                        otherTeamIds={otherTeamIds.filter((id): id is string => Boolean(id))}
                        handleTradeProposalChange={handleTradeProposalChange}
                        proposalPlayerMap={proposalPlayerMap} 
                        proposalPickMap={proposalPickMap} 
                        earliestLockDate={null}
                        practiceSquad={false}
                      />
                    ) : (
                      <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center', 
                        minHeight: '200px',
                        textAlign: 'center',
                        p: 3
                      }}>
                        <Typography variant="body1" color="textSecondary">
                          Loading {tradeTeam?.name || 'team'} roster...
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Collapse>
              </Paper>
            </Grid>
          );
        })}
        
        <Grid item xs={12}>
          <Button 
            variant="outlined" 
            onClick={addTeam}
            disabled={!league?.teams?.length || tradeTeamIds.length >= (league.teams.length - 1)}
            startIcon={<AddIcon />}
            sx={{ 
              mt: 2,
              mb: 2,
              '&:hover': {
                backgroundColor: 'action.hover',
              }
            }}
          >
            Add Team
          </Button>
        </Grid>
      </Grid>


    <ReturnFromIrTradeOption players={players} allInvolvedTeams={allInvolvedTeams} setPlayers={setPlayers} playerMap={playerMap} myTeam={myTeam!} />
    <br/>
    <AdvancedTradeOptions players={players} playerMap={playerMap} teamMap={teamMap} teamPlayerMap={teamPlayerMap} currentSeason={league?.currentSeasonYear} handleDeadCapChange={handleDeadCapChange} />

      {/* Trade Details Section */}
      <Box sx={{ mt: 4, mb: 4 }}>
        <Typography variant="h6" gutterBottom>
          Trade Summary
        </Typography>
        <TradeDetails 
          leagueId={leagueId!} 
          teams={allInvolvedTeams.filter((t): t is Team => t !== undefined)} 
          currentSeason={league?.currentSeasonYear || new Date().getFullYear()} 
          players={(players as Array<TradeProposalPlayer>) || []} 
          picks={(picks as Array<TradeProposalPick>) || []} 
          playerMap={playerMap} 
          teamMap={teamMap} 
          teamPlayerMap={teamPlayerMap} 
          pickMap={pickMap} 
          salaryCap={league?.currentSeason?.nflSeason?.salaryCap || 0} 
          maxRosterSize={maxRosterSize} 
        />
      </Box>

      {/* Action Buttons */}
      <Box sx={{ 
        mt: 3, 
        display: 'flex', 
        justifyContent: 'flex-end', 
        gap: 2,
        position: 'sticky',
        bottom: 20,
        backgroundColor: 'background.paper',
        p: 2,
        borderRadius: 1,
        boxShadow: 1,
        zIndex: 1000
      }}>
        <Button 
          variant="outlined" 
          onClick={clearDraft}
          sx={{
            '&:hover': {
              backgroundColor: 'action.hover',
              borderColor: 'primary.main',
              color: 'primary.main'
            }
          }}
        >
          Clear All
        </Button>
        <Button 
          variant="contained" 
          color="primary" 
          onClick={saveDraft}
          disabled={!tradeTeamIds.length}
          sx={{
            '&:hover': {
              backgroundColor: 'primary.dark',
              boxShadow: 2
            },
            '&:disabled': {
              backgroundColor: 'action.disabledBackground',
              color: 'action.disabled',
              boxShadow: 'none'
            }
          }}
        >
          Save and Review
        </Button>
      </Box>
    </Box>
  );
}

export default React.memo(TradeBuilder);
